<template>
  <div class="container">
    <header>
      <h1 class="text-center gb-page-title">Browse Strategies</h1>
      <BrowseStrategiesFilters
        :subject="subject"
        :barrier="barrier"
        :grades="grades"
        :setting="setting"
        :teaching-type="teachingType"
        :initial-query="query"
        @updateSelection="updateRouteQuery" />
    </header>

    <p class="text-muted">
      <span
        aria-live="polite"
        v-if="!fetchingStrategies && strategies.length > 0">{{ strategies.length }}
        results
      </span>
      <span v-if="fetchingStrategies" aria-live="polite"><i class="fa fa-circle-o-notch fa-spin"/> Loading...</span>
    </p>
    <p
      class="gb-text-2 text-center text-muted empty-state"
      aria-live="polite"
      v-if="!fetchingStrategies && strategies.length === 0">
      No strategies found for the given criteria.</p>
    <section :class="{ 'semitransparent': fetchingStrategies }">
      <div class="row">
        <div
          class="col-md-4 col-sm-6"
          v-for="s of strategies"
          :key="s.uuid">
          <StrategyImageTile :strategy="s" @click.native="strategyClicked(s)" />
        </div>
      </div>
    </section>
    <SubmitFeedback
      :subject="subject"
      type="Browse Strategies Results"/>
  </div>
</template>

<script>
import { getStrategies } from '@/api/strategies'
import BrowseStrategiesFilters from '@/components/strategies/BrowseStrategiesFilters'
import StrategyImageTile from '@/components/strategies/StrategyImageTile'
import SubmitFeedback from '@/components/SubmitFeedback'

import {
  filterProps,
  gradeOptions,
  strategyFiltersPropsMixinFactory
} from '@/components/strategies/strategy_filters_util'
import { mapGetters } from 'vuex'

export default {
  name: 'BrowseStrategies',
  components: {
    StrategyImageTile,
    BrowseStrategiesFilters,
    SubmitFeedback
  },
  mixins: [strategyFiltersPropsMixinFactory({ routeName: 'BrowseStrategiesPage' })],
  props: filterProps,
  data() {
    return {
      strategies: [],
      fetchingStrategies: false,
      fetchingError: null
    }
  },
  methods: {
    async getStrategies() {
      this.fetchingStrategies = true
      this.fetchingError = null

      let tags = [this.subject, this.barrier].filter(t => t).join(',')

      try {
        this.strategies = await getStrategies({
          published: true,
          tags: tags,
          setting: this.setting || undefined,
          teachingType: this.teachingType || undefined,
          grades: (this.grades && this.grades.join(',')) || undefined,
          query: this.query || undefined,
          size: (this.query && 150) || 400,
        })
        this.strategies = this.strategies.hits.hits.map(strategy => {
          return strategy._source
        })
        if (!this.isPremium) {
          // for non-premium users, sorting by access is prioritized over sorting by keyword search relevance
          this.strategies = this.strategies.sort((a) => {
            return a.demo ? -1 : 1
          })
          for (let i=13; i < this.strategies.length; i++) {
            this.strategies[i].demo = false // ensure max 13 unlocked strategies for non-premium
          }
        }
        this.fetchingStrategies = false
      } catch (err) {
        this.fetchingError = err
      }
      this.sendFiltersEvent('Filtered Strategy Results')
    },
    strategyClicked(strategy) {
      this.$sendEvent(
        'Clicked Strategy Result',
        {
          uuid: strategy.uuid,
          title: strategy.title,
          ...this.urlQuery
        },
        {
          cu: strategy.uuid,
          ct: strategy.title
        }
      )
    }
  },
  computed: {
    ...mapGetters('user', ['isPremium']),
  },
  watch: {
    $route(to, from) {
      this.getStrategies()
    }
  },
  created() {
    this.getStrategies()
    this.sendFiltersEvent('Viewed Browse Strategies', {
      ref: this.$route.query.ref
    })
  }
}
</script>

<style scoped>
h1 {
  margin-bottom: 36px;
}
.semitransparent {
  opacity: 0.55;
}
.empty-state {
  margin-top: 32px;
}
</style>
